import { z } from 'zod';
export const overlayLayoutEntrySchema = z.object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    scale: z.number().min(0.25).max(4),
});
export const overlayLayoutSchema = z.record(z.string().min(1), overlayLayoutEntrySchema.nullable());
const SCALED_KEYS = new Set(['fontSize', 'strokeWidth']);
const round1 = (n) => Math.round(n * 10) / 10;
/** Deep-copies `value`, multiplying every numeric fontSize / strokeWidth. */
function scaleTypography(value, scale) {
    if (Array.isArray(value))
        return value.map((v) => scaleTypography(v, scale));
    if (value && typeof value === 'object') {
        const out = {};
        for (const [key, child] of Object.entries(value)) {
            out[key] =
                SCALED_KEYS.has(key) && typeof child === 'number'
                    ? round1(child * scale)
                    : scaleTypography(child, scale);
        }
        return out;
    }
    return value;
}
function applyEntry(block, entry) {
    const scaled = entry.scale === 1
        ? { ...block }
        : scaleTypography(block, entry.scale);
    const existing = block
        .placement;
    const placement = {
        anchor: existing?.anchor ?? 'center',
        x: entry.x,
        y: entry.y,
        ...(existing?.width !== undefined
            ? { width: Math.min(1, existing.width * entry.scale) }
            : {}),
        ...(existing?.height !== undefined
            ? { height: Math.min(1, existing.height * entry.scale) }
            : {}),
    };
    return { ...scaled, placement };
}
/** Pure. Returns a new region tree with each overlay block (in leaf.overlays, recursing through split children) whose id has a non-null entry moved/scaled. Blocks without placement get { anchor: 'center', x, y }. Existing anchor is kept. width/height (when present) are multiplied by scale and clamped to 1. Every numeric `fontSize` and `strokeWidth` anywhere inside the block is multiplied by scale (rounded to 1 decimal). Untouched blocks keep reference identity; with an empty/undefined layout the input root is returned as-is. */
export function applyOverlayLayout(root, layout) {
    if (!layout || Object.values(layout).every((entry) => !entry))
        return root;
    const visit = (region) => {
        if (region.kind === 'leaf') {
            return {
                ...region,
                overlays: region.overlays.map((block) => {
                    const entry = layout[block.id];
                    return entry ? applyEntry(block, entry) : block;
                }),
            };
        }
        return {
            ...region,
            children: region.children.map((child) => ({
                ...child,
                region: visit(child.region),
            })),
        };
    };
    return visit(root);
}
//# sourceMappingURL=overlay-layout.js.map